import { Box, Button, Checkbox, Divider, Typography } from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { CreateDestinationFormValues } from '../../organisms/create-destination/create-destination-form';

import CautionCard from '@/components/atoms/caution-card';
import StepTitle from '@/components/atoms/step-title';
import { CardWithContent } from '@/components/molecules/card-with-content';
import {
  CreateDestinationContext,
  CreateDestinationEvent,
} from '@/features/destinations/utils/machine/create-destination-machine';
import { useOrganizationDevices } from '@/features/shared/hooks/query/use-organization-devices';

interface Props {
  context: CreateDestinationContext;
  send: (event: CreateDestinationEvent) => void;
}

export const Receptions = ({ context, send }: Props) => {
  const { t } = useTranslation(['common', 'createDestination', 'destination']);
  const { control, watch, setValue } = useFormContext<CreateDestinationFormValues>();

  const devices = watch('devices') || [];
  const { data: organizationDevices } = useOrganizationDevices();

  const handleToggleDeviceSelection = ({ id, name }: { id: number; name: string }) => {
    const currentDevices = [...devices];
    const existingDeviceIndex = currentDevices.findIndex(device => device.id === id);

    if (existingDeviceIndex !== -1) {
      setValue(
        'devices',
        currentDevices.filter(device => device.id !== id),
      );
      return;
    }

    setValue('devices', [...currentDevices, { id, name }]);
  };

  return (
    <div>
      <Box
        sx={{
          display: 'grid',
          gap: 1,
          gridAutoFlow: 'row',
        }}>
        <StepTitle
          title={t('common:stepTitle', {
            step: context.step,
            title: t(context.content),
          })}
        />

        <CautionCard
          title={t('common:optional')}
          description={t('createDestination:optionalStepDescription')}
          sx={{ mb: 2 }}
        />

        <Controller
          control={control}
          name="devices"
          render={({ field: { value: _value } }) => (
            <>
              {(organizationDevices || []).map(({ id, name }) => (
                <CardWithContent
                  key={id}
                  isSelected={devices.some(device => device.id === id)}
                  onClick={() => handleToggleDeviceSelection({ id, name })}>
                  <Checkbox
                    checked={devices.some(device => device.id === id)}
                    onClick={event => {
                      event.stopPropagation();
                    }}
                    onChange={event => {
                      event.stopPropagation();
                      handleToggleDeviceSelection({ id, name });
                    }}
                  />
                  <Typography variant="body2" sx={{ ml: 2, flex: 1 }}>
                    {name}
                  </Typography>
                </CardWithContent>
              ))}
              {(organizationDevices || []).length === 0 && (
                <Typography sx={{ p: 2 }} variant="inherit">
                  {t('destination:receptions.noAvailableReceptions')}
                </Typography>
              )}
            </>
          )}
        />

        <Divider sx={{ mt: 2 }} />

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            mt: 3,
          }}>
          <Button
            color="primary"
            onClick={() => send({ type: 'BACK' })}
            size="large"
            sx={{ mr: 2 }}
            type="button"
            variant="text">
            {t('common:button.back')}
          </Button>
          <Button
            color="primary"
            size="large"
            type="button"
            variant="contained"
            onClick={() => send({ type: 'NEXT' })}>
            {t('common:button.nextStep')}
          </Button>
        </Box>
      </Box>
    </div>
  );
};
